import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import OfferPartnerListQuestionsListCard from './OfferPartnerListQuestionsListCard';
import {
  getOfferPartnerQuestions,
  getPartnerQuestionsIsLoaded,
  getOfferPartnerQuestionActions,
  getPartnerQuestionActionsIsLoaded,
  getTotalScore
} from '../../selectors';
import { loadOfferPartnerQuestions, loadOfferPartnerQuestionActions } from '../../actionCreators';
import useDebounce from 'utils/hooks/useDebounce';
import { dummyPartnerIds } from '../../../../../../../constants/dummyPartnerIds';

const OfferPartnerListQuestionnaireContainer = ({
  history,
  documentId,
  siteId,
  partnerId,
  totalScore,
  partnerQuestions,
  openQuestionnaire,
  partnerQuestionsLoaded,
  partnerQuestionActions,
  partnerQuestionActionsLoaded,
  onChangeActiveStep,
  actions
}) => {
  const [cardExpanded, setCardExpanded] = useState(false);
  const onExpand = (_, expanded) => {
    setCardExpanded(!expanded);
  };
  const rowsPerPageOptions = [5, 10, 20];
  const defaultPagination = { page: 1, pageSize: 5, totalCount: 0 };
  const [questionFilter, setQuestionFilter] = useState('');
  const [pagination, setPagination] = useState(defaultPagination);
  const debounceFilterValue = useDebounce(questionFilter, 250);
  const onChangeFilterValue = filterValue => {
    setQuestionFilter(filterValue);
  };
  const [localPartnerQuestions, setLocalPartnerQuestions] = useState(partnerQuestions);
  const [localPartnerQuestionActions, setLocalPartnerQuestionActions] = useState(partnerQuestionActions);

  useEffect(() => {
    if (!partnerQuestionsLoaded) {
      if (documentId != null && siteId != null && partnerId != null) actions.loadOfferPartnerQuestions(documentId, siteId, partnerId);
    }
    if (!partnerQuestionActionsLoaded) {
      if (documentId != null && siteId != null && partnerId != null) actions.loadOfferPartnerQuestionActions(documentId, siteId, partnerId);
    }
  }, [cardExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (openQuestionnaire) setCardExpanded(openQuestionnaire);
  }, [openQuestionnaire]);

  useEffect(() => {
    if (partnerQuestionsLoaded) {
      setPagination(prevPagination => ({
        ...prevPagination,
        totalCount: partnerQuestions.length
      }));
      setLocalPartnerQuestions(getItemsPerPage(partnerQuestions, pagination.pageSize, pagination.page, questionFilter));
    }
  }, [partnerQuestionsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (partnerQuestionActionsLoaded) {
      setLocalPartnerQuestionActions(partnerQuestionActions);
    }
  }, [partnerQuestionActionsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (partnerQuestionsLoaded) {
      updatePageSize(partnerQuestions, pagination, questionFilter);
    }
  }, [debounceFilterValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const onRealBeneficiaryClick = () => {
    onChangeActiveStep(-1);
    history.push(`/offers/${documentId}/${siteId}/partner/${dummyPartnerIds.RealBeneficiaryAssociatedPartner}`);
  };

  const getItemsPerPage = (questions, pageSize, page, filter) => {
    let current = (page - 1) * pageSize;
    const questionsOnPage = questions.slice(current, current + pageSize);
    let result;
    if (filter !== '') {
      result = filterQuestionsByValue(questions, filter);
      setPagination({
        ...defaultPagination,
        totalCount: result.length
      });
      setQuestionFilter(filter);
    } else {
      result = questionsOnPage;
      setPagination({
        ...defaultPagination,
        totalCount: questions.length,
        pageSize: pageSize,
        page: page
      });
      setQuestionFilter('');
    }
    return result;
  };

  const updatePageSize = (questions, pagination, filterValue) => {
    setLocalPartnerQuestions(getItemsPerPage(questions, pagination.pageSize, pagination.page, filterValue));
  };

  const filterQuestionsByValue = (questions, filter) => {
    const result = questions.filter(o =>
      Object.keys(o).some(k => {
        if (o[k] !== null && k === 'questionName') return o[k].toString().toLowerCase().includes(filter.toString().toLowerCase());
        return null;
      })
    );
    return result;
  };

  const onChangePage = page => {
    const newPage = ++page;
    setPagination({ ...pagination, page: newPage });
    updatePageSize(partnerQuestions, { ...pagination, page: newPage }, questionFilter);
  };

  const onChangeRowsPerPage = size => {
    setPagination({ ...pagination, pageSize: size, page: defaultPagination.page });
    updatePageSize(partnerQuestions, { ...pagination, pageSize: size, page: defaultPagination.page }, questionFilter);
  };

  const onPaginationAndFiltersRefresh = () => {
    updatePageSize(partnerQuestions, { ...pagination, pageSize: defaultPagination.pageSize }, '');
    setPagination({ ...defaultPagination, totalCount: partnerQuestions.length });
  };

  return (
    <OfferPartnerListQuestionsListCard
      partnerQuestions={localPartnerQuestions}
      partnerQuestionsLoaded={partnerQuestionsLoaded}
      partnerQuestionActions={localPartnerQuestionActions}
      partnerQuestionActionsLoaded={partnerQuestionActionsLoaded}
      onRealBeneficiaryClick={onRealBeneficiaryClick}
      onExpand={onExpand}
      partnerId={partnerId}
      documentId={documentId}
      totalScore={totalScore}
      siteId={siteId}
      cardExpanded={cardExpanded}
      history={history}
      onChangeActiveStep={onChangeActiveStep}
      editQuestionnaireMode={false}
      pagination={pagination}
      fullTextFilter={questionFilter}
      onChangeFilterValue={onChangeFilterValue}
      onChangeRowsPerPage={onChangeRowsPerPage}
      onChangePage={onChangePage}
      onRefresh={onPaginationAndFiltersRefresh}
      rowsPerPageOptions={rowsPerPageOptions}
    />
  );
};

OfferPartnerListQuestionnaireContainer.propTypes = {
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  partnerId: PropTypes.number,
  totalScore: PropTypes.number,
  openQuestionnaire: PropTypes.bool.isRequired,
  actions: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  partnerQuestions: PropTypes.array.isRequired,
  partnerQuestionsLoaded: PropTypes.bool.isRequired,
  partnerQuestionActions: PropTypes.array.isRequired,
  partnerQuestionActionsLoaded: PropTypes.bool.isRequired,
  onChangeActiveStep: PropTypes.func.isRequired
};

function mapStateToProps(state, ownProps) {
  const partnerQuestions = getOfferPartnerQuestions(state, ownProps.documentId, ownProps.siteId, ownProps.partnerId);
  const totalScore = getTotalScore(state, ownProps.documentId, ownProps.siteId, ownProps.partnerId);
  const partnerQuestionsLoaded = getPartnerQuestionsIsLoaded(state, ownProps.documentId, ownProps.siteId, ownProps.partnerId);
  const partnerQuestionsLoading = !partnerQuestionsLoaded;

  const partnerQuestionActions = getOfferPartnerQuestionActions(state, ownProps.documentId, ownProps.siteId, ownProps.partnerId);
  const partnerQuestionActionsLoaded = getPartnerQuestionActionsIsLoaded(state, ownProps.documentId, ownProps.siteId, ownProps.partnerId);
  const partnerQuestionActionsLoading = !partnerQuestionActionsLoaded;

  return {
    partnerQuestions,
    partnerQuestionsLoaded,
    partnerQuestionsLoading,
    partnerQuestionActions,
    partnerQuestionActionsLoaded,
    partnerQuestionActionsLoading,
    totalScore
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ loadOfferPartnerQuestions, loadOfferPartnerQuestionActions }, dispatch),
    dispatch
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(OfferPartnerListQuestionnaireContainer));
